import { concat } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import type { SafeString } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismIcon, PrismIconBrand } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import type { IconBrandName } from '@mylighthouse/prism-ember/types/components/prism/icon-brand';
import dayjs from 'dayjs';
import { hash, task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import type { Rate } from 'lumen/models';
import type { PeriodRate } from 'lumen/models/period';
import type { HotelOrMarket } from 'lumen/services/hotel-and-market';
import type { requestParams } from 'lumen/utils/constants/rate-constants';
import {
  checkLosRestriction,
  getLosRestriction,
} from 'lumen/utils/los-restriction';
import {
  mapQueryParamsToAPIParams,
  requestRates,
} from 'lumen/utils/rates-request';
import { timeout } from 'lumen/utils/waiters';

interface MappedRate {
  extractDatetimeFromNow: string;
  formattedRate?: string | SafeString | null;
  hasData: boolean;
  isLoadingRestrictionRate: boolean;
  is_out_of_sync: boolean;
  ota: IconBrandName;
  restrictedRate?: string | SafeString | null;
  roomName: string;
  showRateType: boolean;
  strRateType: string;
}

interface HotelTooltipsTooltipRateAllchannelsSignature {
  Args: {
    displayOverModal?: boolean;
    hotel: HotelOrMarket;
    isFuture: boolean | undefined;
    isUpdating?: boolean;
    requestParams?: requestParams;
    value: PeriodRate[];
  };
}

export default class HotelTooltipsTooltipRateAllchannels extends Component<HotelTooltipsTooltipRateAllchannelsSignature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare rates: Services['rates'];
  @service declare store: Services['store'];

  @tracked isLoadingRestrictionRate = false;
  @tracked ownRestrictionRates: Record<string, Rate[]> = {};

  getCompetitorRatesWithDifferentLosTask = task(
    { restartable: true },
    async (): Promise<void> => {
      if (this.losRestrictions.length) {
        try {
          // Prevent calling the API too often when sliding the cursor across the window
          await timeout();

          const startDate = this.args.value?.[0]?.period?.split(',')[0];
          const promiseHash: Record<string, Promise<Rate[]>> = {};

          if (!startDate) {
            throw new Error('Start date is not available');
          }

          let foundRates = false;
          const ownRestrictionRates: Record<string, Rate[]> = {};
          this.losRestrictions.forEach((losRestriction) => {
            ownRestrictionRates[losRestriction] = [];
            const rates = this.rates.getCachedLosRestrictionRates(
              startDate,
              losRestriction,
            ) as Rate[];
            rates.forEach((rate) => {
              if (
                // @ts-expect-error: 'rate.hotelinfo.id' is possibly 'null'.
                rate.hotelinfo.id.toString() ===
                this.args.value?.[0]?.hotelinfo.toString()
              ) {
                ownRestrictionRates[losRestriction]?.push(rate);
                foundRates = true;
              }
            });
          });

          if (!foundRates) {
            this.losRestrictions.forEach((losRestriction) => {
              const queryParams = { ...this.args.requestParams };
              queryParams.los = losRestriction;

              const apiParams = mapQueryParamsToAPIParams(
                queryParams,
                startDate,
                startDate,
              );
              const ratesQuery = requestRates(
                'rate',
                // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
                this.args.hotel.id,
                apiParams,
                this.store,
              );

              promiseHash[losRestriction] = ratesQuery;
            });

            // Pauses the task until the yield is resolved
            const losRates = await hash(promiseHash);

            this.losRestrictions.forEach((losRestriction) => {
              const rates = losRates[losRestriction] ?? [];
              if (rates) {
                this.rates.setCachedLosRestrictionRates(rates);
                rates.forEach((rate) => {
                  const hotelInfoId = rate.hotelinfo?.id;
                  if (
                    // @ts-expect-error: 'hotelInfoId' is possibly 'null'.
                    hotelInfoId.toString() ===
                    this.args.value?.[0]?.hotelinfo.toString()
                  ) {
                    ownRestrictionRates[losRestriction]?.push(rate);
                  }
                });
              }
            });
          }

          this.ownRestrictionRates = ownRestrictionRates;
          this.isLoadingRestrictionRate = false;
        } catch (error) {
          this.errorTracking.captureException(error);
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          this.alerting.showErrorAlert();
        }
        // Will ONLY execute on a successful try block (to force execution: use finally block)
        this.isLoadingRestrictionRate = false;
      } else {
        this.isLoadingRestrictionRate = false;
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get allChannelsRates() {
    const rates: MappedRate[] = [];

    this.args.value.forEach((rate) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const losRestriction = getLosRestriction(rate);

      const mappedRate: MappedRate = {
        extractDatetimeFromNow: dayjs.utc(rate.extract_datetime).fromNow(),
        strRateType: this.rates.getRateTypeString(rate),
        showRateType:
          rate.message !== 'rates.soldout' &&
          rate.message !== 'rates.roomtypes.unavailable',
        hasData: rate.message !== 'general.missing',
        formattedRate: this.rates.formatRate(rate, this.args.hotel),
        roomName: rate.room_name,
        ota: rate.ota as IconBrandName,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        isLoadingRestrictionRate:
          this.isLoadingRestrictionRate && checkLosRestriction(rate),
        is_out_of_sync: rate.is_out_of_sync,
      };

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (this.ownRestrictionRates[losRestriction]) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.ownRestrictionRates[losRestriction]?.forEach((restrictedRate) => {
          if (
            rate.ota === 'bookingdotcom' &&
            restrictedRate.ota?.id === rate.ota
          ) {
            mappedRate.restrictedRate = this.rates.formatRate(
              restrictedRate,
              this.args.hotel,
            );

            mappedRate.roomName = restrictedRate.roomName;
            mappedRate.strRateType =
              this.rates.getRateTypeString(restrictedRate);
            mappedRate.extractDatetimeFromNow = dayjs
              .utc(restrictedRate.extractDatetime)
              .fromNow();
          }
        });
      }

      rates.push(mappedRate);
    });

    return rates;
  }

  /**
   * returns all los that needs to be requested
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get losRestrictions() {
    const losKeysToFetch: Set<number> = new Set();

    this.args.value.forEach((rate) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const hasLosRestriction = checkLosRestriction(rate);

      if (hasLosRestriction) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const los = getLosRestriction(rate);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        losKeysToFetch.add(los);
      }
    });

    // Set to array
    return [...losKeysToFetch];
  }

  @action
  handleShow(): void {
    if (this.losRestrictions) {
      this.isLoadingRestrictionRate = true;
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.getCompetitorRatesWithDifferentLosTask.perform();
    }
  }

  <template>
    <EmberTooltip
      @delay={{25}}
      @onShow={{this.handleShow}}
      @side="bottom"
      @tooltipClass={{concat
        "base-tooltip rates-table-tooltip"
        (if @displayOverModal " display-over-modal")
      }}
    >
      <div class="rates-table-tooltip-container">
        <table cellspacing="0">
          <thead>
            <tr>
              <th>
                {{t "general.room_name"}}
              </th>
              <th>
                {{t "rates.rate"}}
              </th>
              <th>
                {{t "settings.reports.rate_type"}}
              </th>
              {{#if @isFuture}}
                <th>
                  {{t "general.updated"}}
                </th>
              {{/if}}
              <th>
                {{t "parity.source"}}
              </th>
            </tr>
          </thead>
          <tbody>
            {{#each this.allChannelsRates as |rate|}}
              <tr>
                {{#if (and rate.isLoadingRestrictionRate rate.hasData)}}
                  <td class="rate-tooltip-roomtype truncate">
                    <p class="loading-state-other-los-rate-table"></p>
                  </td>
                  <td class="rate-tooltip-formatted-rate">
                    <div class="formatted-rate-container flex justify-between">
                      <p class="loading-state-other-los-rate-table"></p>
                    </div>
                  </td>
                  <td>
                    <p class="loading-state-other-los-rate-table"></p>
                  </td>
                {{else}}
                  <td class="rate-tooltip-roomtype truncate">
                    {{rate.roomName}}
                  </td>
                  <td class="rate-tooltip-formatted-rate">
                    {{rate.formattedRate}}
                    {{#if rate.restrictedRate}}
                      ({{rate.restrictedRate}})
                    {{/if}}
                  </td>
                  <td>
                    {{#if (and rate.showRateType rate.hasData)}}
                      {{rate.strRateType}}
                    {{/if}}
                  </td>
                {{/if}}
                {{#if @isFuture}}
                  <td>
                    {{#if rate.hasData}}
                      <div
                        class="rate-tooltip-extract-time-cell flex items-center gap-100"
                      >
                        {{rate.extractDatetimeFromNow}}
                        {{#if (and rate.is_out_of_sync (not @isUpdating))}}
                          <PrismIcon
                            @name="sync-disabled"
                            @size="extra-small"
                          />
                        {{/if}}
                      </div>
                    {{/if}}
                  </td>
                {{/if}}
                <td>
                  <PrismIconBrand @name={{rate.ota}} />
                </td>
              </tr>
            {{/each}}
          </tbody>
        </table>
      </div>
    </EmberTooltip>
  </template>
}
